import React, { useState, useMemo } from 'react';
import { Card, CardContent, Box, Typography, List, ListItemButton, Button, Divider } from '@mui/material';
import { Timeline as TimelineIcon, FiberManualRecord } from '@mui/icons-material';

const MEANINGFUL_EVENTS = new Set([
  'CHECK_IN_COMPLETED',
  'GOAL_COMPLETED',
  'GOAL_CREATED',
  'ALERT_CREATED',
  'AI_SAFETY_TAG_DETECTED',
  'ALERT_ACKNOWLEDGED',
  'THERAPIST_TAKE_CONTROL',
  'THERAPIST_HANDBACK_TO_AI',
  'ASSESSMENT_COMPLETED',
  'ASSESSMENT_STARTED',
  'JOURNAL_CREATED',
]);

const formatEventType = (type) => {
  const map = {
    'MESSAGE_SENT': 'Message',
    'GOAL_COMPLETED': 'Goal completed',
    'GOAL_CREATED': 'New goal',
    'CHECK_IN_COMPLETED': 'Check-in',
    'JOURNAL_CREATED': 'Journal',
    'ALERT_CREATED': 'Alert raised',
    'ALERT_ACKNOWLEDGED': 'Alert ack\'d',
    'AI_SAFETY_TAG_DETECTED': 'Safety flag',
    'THERAPIST_TAKE_CONTROL': 'Took control',
    'THERAPIST_HANDBACK_TO_AI': 'Handed back',
    'ASSESSMENT_COMPLETED': 'Assessment done',
    'ASSESSMENT_STARTED': 'Assessment started',
  };
  return map[type] || type?.replace(/_/g, ' ').toLowerCase() || 'Activity';
};

const formatTime = (ts) => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

const getDayLabel = (ts) => {
  const d = new Date(ts);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const itemDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());

  if (itemDay.getTime() === today.getTime()) return 'Today';
  if (itemDay.getTime() === yesterday.getTime()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const isSafetyItem = (eventType) =>
  eventType && (/SAFETY|CRISIS|ALERT/.test(eventType));

const getDetailText = (details) => {
  if (!details) return null;
  if (typeof details === 'string') return details;
  if (details.goalTitle) return `"${details.goalTitle}"`;
  if (details.moodRating) return `Mood ${details.moodRating}/5`;
  if (details.score !== undefined) return `Score: ${details.score}`;
  if (details.mode) return `(${details.mode} mode)`;
  return null;
};

/**
 * ActivityStream - shows meaningful events with day groupings.
 * @param {boolean} compact - if true, renders as a sidebar mini-feed (no card wrapper, limit 5 items)
 * @param {function} onViewAll - callback for "View all" link in compact mode
 */
export default function ActivityStream({ activities, onNavigateToClient, compact = false, onViewAll }) {
  const [showAll, setShowAll] = useState(false);

  const filteredItems = useMemo(() => {
    const items = activities || [];
    return items.filter(a => MEANINGFUL_EVENTS.has(a.eventType));
  }, [activities]);

  const maxItems = compact ? 5 : (showAll ? 20 : 10);
  const visibleItems = filteredItems.slice(0, maxItems);
  const hasMore = !compact && !showAll && filteredItems.length > 10;

  // Group visible items by day
  const groupedItems = useMemo(() => {
    const groups = [];
    let currentLabel = null;
    for (const item of visibleItems) {
      const label = getDayLabel(item.timestamp);
      if (label !== currentLabel) {
        groups.push({ type: 'header', label });
        currentLabel = label;
      }
      groups.push({ type: 'item', data: item });
    }
    return groups;
  }, [visibleItems]);

  // When empty, render nothing — don't waste space with an empty card
  if (!filteredItems.length) {
    return null;
  }

  const renderList = () => (
    <List dense disablePadding>
      {groupedItems.map((entry, idx) => {
        if (entry.type === 'header') {
          return (
            <Typography
              key={`hdr-${entry.label}`}
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                px: 1,
                pt: idx === 0 ? 0 : 1,
                pb: 0.25,
                fontWeight: 600,
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {entry.label}
            </Typography>
          );
        }
        const activity = entry.data;
        const safety = isSafetyItem(activity.eventType);
        return (
          <ListItemButton
            key={activity._id}
            onClick={() => onNavigateToClient(activity.clientId || activity.userId)}
            sx={{
              py: 0.25,
              px: 1,
              borderLeft: safety ? '3px solid #d32f2f' : '3px solid transparent',
              '&:hover': { bgcolor: 'action.hover' },
              minHeight: 28,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
              {safety && (
                <FiberManualRecord sx={{ fontSize: 6, color: '#d32f2f', flexShrink: 0 }} />
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: 'monospace', width: 40, flexShrink: 0, fontSize: '0.7rem' }}
              >
                {formatTime(activity.timestamp)}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={safety ? 'bold' : 'medium'}
                sx={{ flexShrink: 0, fontSize: compact ? '0.8rem' : '0.875rem' }}
              >
                {activity.clientName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                &middot;
              </Typography>
              <Typography
                variant="body2"
                fontWeight={safety ? 'bold' : 'regular'}
                sx={{ flexShrink: 0, fontSize: compact ? '0.8rem' : '0.875rem' }}
              >
                {formatEventType(activity.eventType)}
              </Typography>
              {!compact && getDetailText(activity.details) && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                    &middot;
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {getDetailText(activity.details)}
                  </Typography>
                </>
              )}
            </Box>
          </ListItemButton>
        );
      })}
    </List>
  );

  // Compact mode: no Card wrapper, sidebar-style
  if (compact) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TimelineIcon sx={{ fontSize: 18, color: 'action.active' }} />
            <Typography variant="subtitle2" fontWeight="bold">Activity</Typography>
          </Box>
          <Box sx={{ maxHeight: 220, overflow: 'hidden' }}>
            {renderList()}
          </Box>
          {filteredItems.length > 5 && onViewAll && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
              <Button size="small" onClick={onViewAll} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                View all activity →
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full mode
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <TimelineIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight="bold">Recent Activity</Typography>
        </Box>

        <Box sx={{ maxHeight: showAll ? 400 : 200, overflow: 'hidden' }}>
          {renderList()}
        </Box>

        {hasMore && (
          <>
            <Divider sx={{ mt: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button size="small" onClick={() => setShowAll(true)} sx={{ textTransform: 'none' }}>
                Show More
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
